import { Download, History, Printer, Sheet as SheetIcon } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { HistoryDrawer } from "@/components/HistoryDrawer";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils/cn";
import { IssueIcon, thematicClasses } from "@/lib/thematic";
// One control for all three directories, each opening as an in-context
// slide-over (parity with the old app's répertoire drawer).
import { ResourceMenu, hasAnyResource } from "@/features/resources";

import {
  downloadPlanExport,
  useActionPlanHistory,
  useUpdateActions,
} from "../../api/hooks";
import {
  ACTION_STATUSES,
  type ActionPlanAction,
  type ActionPlanDetail,
  type ActionStatus,
} from "../../api/types";
import { actionName, planActions } from "../../lib/selection";
import { StepIntro } from "./StepIntro";

const STATUS_STYLE: Record<ActionStatus, string> = {
  done: "bg-success/10 text-success",
  in_progress: "bg-theme-economic-soft text-theme-economic-ink",
  not_done: "bg-secondary text-muted-foreground",
};

function StatusBar({
  status,
  count,
  total,
}: {
  status: ActionStatus;
  count: number;
  total: number;
}) {
  const { t } = useTranslation();
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  const fill =
    status === "done"
      ? "bg-success"
      : status === "in_progress"
        ? "bg-theme-economic"
        : "bg-muted-foreground";
  const label =
    status === "done"
      ? "text-success"
      : status === "in_progress"
        ? "text-theme-economic-ink"
        : "text-muted-foreground";
  return (
    <div className="rounded-xl border p-4">
      <div className="flex items-center justify-between text-sm font-semibold">
        <span className={label}>{t(`actionPlans.status.${status}`)}</span>
        <span className="tabular-nums text-muted-foreground">
          {pct}% · {count}/{total}
        </span>
      </div>
      <div className="mt-2 h-2 overflow-hidden rounded-full bg-muted">
        <div className={cn("h-full rounded-full", fill)} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

/**
 * Step 5 — "Track progress" (legacy "Suivi du plan d'action"). Global
 * advancement across the three statuses, then a per-thematic table where each
 * action's status is set inline; plus PDF / Excel / Print export.
 */
export function TrackStep({ plan }: { plan: ActionPlanDetail }) {
  const { t } = useTranslation();
  const updateActions = useUpdateActions(plan.id);
  const [busy, setBusy] = useState<null | "pdf" | "excel">(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  // Only fetched once the drawer is opened — the feed is read-only and nothing
  // else on the page needs it.
  const history = useActionPlanHistory(historyOpen ? plan.id : null);
  const actionHasResources = (a: ActionPlanAction) =>
    Boolean(a.original_action) && hasAnyResource(plan, a);
  // One "Ressources" column replacing the separate Experts/Outils columns. Shown
  // only when some action actually has something — an enabled directory alone
  // would render a column of empty cells, and the Django table (which gates on
  // `resource_availability.any_resources`) omits it entirely in that case.
  const resourceColumn = planActions(plan).some(actionHasResources);

  const selected = planActions(plan).filter((a) => a.is_selected);
  const total = selected.length;
  const counts: Record<ActionStatus, number> = {
    not_done: 0,
    in_progress: 0,
    done: 0,
  };
  for (const a of selected) counts[a.status ?? "not_done"] += 1;

  const exportPlan = async (format: "pdf" | "excel", ext: string) => {
    setBusy(format);
    try {
      const safe = (plan.name ?? "plan-action").replace(/[^\w -]/g, "_");
      await downloadPlanExport(plan.id, format, `${safe}.${ext}`);
    } catch {
      toast.error(t("actionPlans.visualization.exportError"));
    } finally {
      setBusy(null);
    }
  };

  const renderActionLabel = (action: ActionPlanAction) => (
    <span className="font-medium">{actionName(action)}</span>
  );

  // OAC-440 — a single "Ressources" column: one dropdown per action listing the
  // favorites entry for each available resource (experts / tools). Each opens the
  // directory in favorites-only mode; when the action has no favorite yet, that
  // directory shows a message + a "See all" button to switch to the full list.
  // Renders nothing when the action has neither experts nor tools.
  const renderResourceCell = (action: ActionPlanAction) => (
    <ResourceMenu
      actionId={action.original_action}
      actionName={actionName(action)}
      directories={plan}
      availability={action}
      variant="short"
      favoritesDefault
    />
  );

  const renderStatusSelect = (action: ActionPlanAction) => (
    <select
      aria-label={t("actionPlans.visualization.status")}
      value={action.status ?? "not_done"}
      onChange={(e) =>
        updateActions.mutate([
          { id: action.id, body: { status: e.target.value as ActionStatus } },
        ])
      }
      className={cn(
        "rounded-full border-0 px-3 py-1 text-xs font-bold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        STATUS_STYLE[action.status ?? "not_done"],
      )}
    >
      {ACTION_STATUSES.map((s) => (
        <option key={s} value={s} className="bg-background text-foreground">
          {t(`actionPlans.status.${s}`)}
        </option>
      ))}
    </select>
  );

  return (
    <div className="space-y-6">
      {/* The exports belong beside the step's title rather than under it: they
          act on the whole plan, not on anything below. */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <StepIntro
            heading={t("actionPlans.visualization.heading")}
            help={t("actionPlans.visualization.help")}
          />
        </div>
        <div className="flex shrink-0 flex-wrap gap-2">
          <Button variant="outline" disabled={busy !== null} onClick={() => void exportPlan("pdf", "pdf")}>
            <Download aria-hidden />
            {busy === "pdf"
              ? t("actionPlans.visualization.exporting")
              : t("actionPlans.visualization.downloadPdf")}
          </Button>
          <Button variant="outline" disabled={busy !== null} onClick={() => void exportPlan("excel", "xlsx")}>
            <SheetIcon aria-hidden />
            {busy === "excel"
              ? t("actionPlans.visualization.exporting")
              : t("actionPlans.visualization.downloadExcel")}
          </Button>
          <Button variant="outline" onClick={() => window.print()}>
            <Printer aria-hidden />
            {t("actionPlans.visualization.print")}
          </Button>
          <Button variant="outline" onClick={() => setHistoryOpen(true)}>
            <History aria-hidden />
            {t("actionPlans.visualization.history")}
          </Button>
        </div>
      </div>

      {historyOpen && (
        <HistoryDrawer
          title={plan.name || t("actionPlans.visualization.historyTitle")}
          onClose={() => setHistoryOpen(false)}
          query={history}
        />
      )}

      {total === 0 ? (
        <Card>
          <CardContent className="py-6">
            <p className="text-sm text-muted-foreground">
              {t("actionPlans.visualization.noSelected")}
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-3">
            <StatusBar status="not_done" count={counts.not_done} total={total} />
            <StatusBar status="in_progress" count={counts.in_progress} total={total} />
            <StatusBar status="done" count={counts.done} total={total} />
          </div>

          {plan.action_plan_thematics.map((thematic) => {
            const tc = thematicClasses(thematic.css_class);
            return thematic.action_plan_issues.map((issue) => {
              const rows = issue.action_plan_goals.flatMap((g) =>
                g.action_plan_actions.filter((a) => a.is_selected),
              );
              if (rows.length === 0) return null;
              return (
                <div key={issue.id} className="overflow-hidden rounded-xl border">
                  <div className={cn("flex items-center gap-2.5 px-4 py-3 text-sm font-bold", tc.softBg, tc.ink)}>
                    <IssueIcon icon={issue.icon} className="size-4 shrink-0" aria-hidden />
                    {issue.name}
                  </div>
                  {/* Mobile: one card per action; the 6-column table is too wide. */}
                  <ul className="space-y-2 p-3 md:hidden">
                    {rows.map((action) => (
                      <li key={action.id} className="rounded-lg border p-3">
                        <div>{renderActionLabel(action)}</div>
                        <div className="mt-2">{renderStatusSelect(action)}</div>
                        {actionHasResources(action) && (
                          <div className="mt-3 flex items-center gap-2">
                            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                              {t("actionPlans.visualization.resources")}
                            </span>
                            {renderResourceCell(action)}
                          </div>
                        )}
                        <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
                          <dt className="text-muted-foreground">
                            {t("actionPlans.visualization.responsible")}
                          </dt>
                          <dd>{action.responsible_name || "—"}</dd>
                          <dt className="text-muted-foreground">
                            {t("actionPlans.visualization.deadline")}
                          </dt>
                          <dd className="tabular-nums">{action.deadline || "—"}</dd>
                          <dt className="text-muted-foreground">
                            {t("actionPlans.visualization.target")}
                          </dt>
                          <dd>{action.target || "—"}</dd>
                          <dt className="text-muted-foreground">
                            {t("actionPlans.visualization.estimatedCost")}
                          </dt>
                          <dd className="tabular-nums">
                            {action.estimated_cost != null ? `${action.estimated_cost} $` : "—"}
                          </dd>
                        </dl>
                      </li>
                    ))}
                  </ul>

                  <div className="hidden overflow-x-auto md:block">
                    {/* Fixed layout with the trailing columns sized to their
                        content: one table per issue, so auto layout let every
                        table pick its own width for the wording column and the
                        step read as a row of misaligned tables. The wording
                        column now takes the same remainder in each. */}
                    <table className="w-full min-w-[960px] table-fixed border-collapse text-sm">
                      <thead>
                        <tr className="border-b bg-muted/40 text-left text-xs font-bold uppercase tracking-wide text-muted-foreground">
                          <th className="px-3 py-2">{t("actionPlans.actions.heading")}</th>
                          <th className="w-32 px-3 py-2">{t("actionPlans.visualization.status")}</th>
                          <th className="w-28 px-3 py-2">{t("actionPlans.visualization.responsible")}</th>
                          <th className="w-24 px-3 py-2">{t("actionPlans.visualization.deadline")}</th>
                          <th className="w-36 px-3 py-2">{t("actionPlans.visualization.target")}</th>
                          <th className="w-24 px-3 py-2 text-right">{t("actionPlans.visualization.estimatedCost")}</th>
                          {resourceColumn && (
                            <th className="w-28 px-2 py-2 text-center">
                              {t("actionPlans.visualization.resources")}
                            </th>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((action) => (
                          <tr key={action.id} className="border-b last:border-0 align-top">
                            <td className="px-3 py-2.5">{renderActionLabel(action)}</td>
                            <td className="px-3 py-2.5">{renderStatusSelect(action)}</td>
                            <td className="px-3 py-2.5 text-muted-foreground">
                              {action.responsible_name || "—"}
                            </td>
                            <td className="px-3 py-2.5 tabular-nums text-muted-foreground">
                              {action.deadline || "—"}
                            </td>
                            <td className="px-3 py-2.5 text-muted-foreground">
                              {action.target || "—"}
                            </td>
                            <td className="px-3 py-2.5 text-right tabular-nums text-muted-foreground">
                              {action.estimated_cost != null ? `${action.estimated_cost} $` : "—"}
                            </td>
                            {resourceColumn && (
                              <td className="px-2 py-2.5 text-center">
                                {renderResourceCell(action)}
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            });
          })}
        </>
      )}

    </div>
  );
}
